import React, { useCallback, useEffect } from 'react'
import { FileUploadOverlay } from 'ComponentLibrary/src/molecules/FileUploadModal'
import { createPortal } from 'react-dom'
import { isFileAboveMaxSize } from 'ComponentLibrary/src/common/files'

type FileDropAreaProps = {
    onDataDrop?: (areaId?: string, files?: File[]) => void
    id?: string
    setIsOver: (isOver: boolean) => void
    handleError?: (error: Response | Error) => void
    allowedFileTypes?: string[]
    maxFileSizeBytes?: number
    fileSizeErrorMessage?: string
    onFileValidationError?: (message: string) => void
} & (
    | {
          isOver: boolean
          dragAreaRef?: React.RefObject<HTMLDivElement | null>
          children?: never
          width?: never
          height?: never
      }
    | {
          // This exists for backwards compatibility, please remove when all usages are updated
          children: React.ReactNode
          width?: string
          height?: string
          isOver?: never
          dragAreaRef?: never
      }
)

const FileDropArea = ({
    children,
    onDataDrop,
    id,
    isOver,
    setIsOver,
    handleError,
    width,
    height,
    allowedFileTypes,
    maxFileSizeBytes,
    fileSizeErrorMessage,
    onFileValidationError,
    dragAreaRef,
}: FileDropAreaProps) => {
    const handleDragOver = useCallback(
        <T extends DragEvent | React.DragEvent>(e: T) => {
            if (e?.dataTransfer?.items[0]?.kind === 'file') {
                setIsOver(true)
                e.preventDefault()
            }
        },
        [setIsOver]
    )

    const handleDrop = useCallback(
        <T extends DragEvent | React.DragEvent>(e: T) => {
            e.preventDefault()
            const items = e?.dataTransfer?.items || []
            const files: File[] = []
            let validationErrorMessage: string | undefined
            // items isn't a strict array, for loop needed
            for (let i = 0; i < items.length; i++) {
                if (items[i].kind === 'file') {
                    if (allowedFileTypes === undefined || allowedFileTypes?.includes(items[i].type)) {
                        const file = items[i].getAsFile()
                        if (file !== null) {
                            if (maxFileSizeBytes !== undefined && isFileAboveMaxSize(file, maxFileSizeBytes)) {
                                validationErrorMessage = fileSizeErrorMessage ?? `Die Datei "${file.name}" ist zu groß.`
                            } else {
                                files.push(file)
                            }
                        }
                    }
                }
            }
            try {
                setIsOver(false)
                validationErrorMessage && onFileValidationError?.(validationErrorMessage)
                files.length > 0 && onDataDrop?.(id, files)
            } catch (error) {
                handleError?.(error as Error)
            }
        },
        [allowedFileTypes, fileSizeErrorMessage, handleError, id, maxFileSizeBytes, onDataDrop, onFileValidationError, setIsOver]
    )

    const handleDragLeave = useCallback(() => {
        setIsOver(false)
    }, [setIsOver])

    useEffect(() => {
        if (dragAreaRef?.current) {
            dragAreaRef.current.ondragover = handleDragOver
            dragAreaRef.current.ondrop = handleDrop
            dragAreaRef.current.ondragleave = handleDragLeave
        }
    }, [dragAreaRef, handleDragLeave, handleDragOver, handleDrop, isOver])

    if (isOver && dragAreaRef?.current) {
        return createPortal(<FileUploadOverlay />, dragAreaRef.current, 'filedroparea')
    }

    return (
        <div
            onDragOver={handleDragOver}
            onDrop={handleDrop}
            onDragLeave={handleDragLeave}
            style={{ width: width || 'fit-content', height: height || 'fit-content' }}
        >
            {children}
        </div>
    )
}

export default FileDropArea
